import { FC, useLayoutEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useLocationStore } from '../../stores/locationStore';

/**
 * Deep-link entry point for troubleshooting guides:
 * `/guide/:flowId?location=:location`.
 *
 * Captures the target flow id, then redirects to the normal equipment overview
 * URL and signals it to auto-open the troubleshooting drawer. The `location`
 * query param is optional.
 *
 * We redirect rather than render the overview here on purpose: `/guide/:flowId`
 * is a distinct route from `/equipment/:location?`, so this component only
 * exists to normalize the deep link into the overview's URL (and pass along
 * `openTroubleshooting` state) and let the router mount `EquipmentOverview`.
 */
export const GuideDeepLink: FC = () => {
  const { flowId } = useParams<{ flowId?: string }>();
  const [searchParams] = useSearchParams();
  const location = searchParams.get('location') ?? undefined;
  const { activeLocation, setActiveLocation } = useLocationStore();
  const [captured, setCaptured] = useState(false);

  useLayoutEffect(() => {
    if (flowId) {
      // The troubleshooting drawer resolves its flow from localStorage.
      localStorage.setItem('flowId', flowId);
    }
    // Sync the store to the deep-linked location before redirecting. The flow
    // fetch is location-scoped, so if we let the overview sync activeLocation
    // from the URL after the drawer opens, the request races and can fire with
    // the previous location, 404-ing the flow.
    if (location && location !== activeLocation) {
      setActiveLocation(location);
    }
    setCaptured(true);
  }, [flowId, location, activeLocation, setActiveLocation]);

  // Wait until the flow id is persisted before redirecting so the overview can
  // open the drawer for it.
  if (!captured) return null;

  // Location is optional; fall back to the user's current active location.
  const targetLocation = location ?? activeLocation;
  const to = targetLocation ? `/equipment/${targetLocation}` : '/equipment';

  return (
    <Navigate
      to={to}
      replace
      state={flowId ? { openTroubleshooting: true } : undefined}
    />
  );
};

export default GuideDeepLink;
